import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, pairwise, takeUntil, tap, startWith, finalize } from 'rxjs/operators';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import {
  MixModeType,
  OutgoingMessageType,
  StreamingForwardMessage,
  StreamingMixModeMessage,
  StreamingStopForwardingMessage,
} from 'messages/websocket.messages';

import { DEFAUlT_MIX_VOLUME } from '../../constants/volume';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { RtcStatsStreamService } from '../rtc-stats-stream';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { EasingService, linearEasing } from './../easing-service';
import { MixSubscriber } from './mix-subscriber';

/**
 * Service for managing the audio mix subscription.
 */
@Injectable({
  providedIn: 'root',
})
export class PresentationAudioService extends Loggable {
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isAudioForwarding = false;
  private mixSubscriber?: MixSubscriber;
  private mixVolume = DEFAUlT_MIX_VOLUME;

  public constructor(
    private videoRoomService: VideoRoomService,
    private websocketService: WebsocketService,
    private rtcStatsStreamService: RtcStatsStreamService,
    private easingService: EasingService,

    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('PresentationAudioService');
  }

  public init(): void {
    this.info('Initializing');
    this.destroyed$ = new ReplaySubject(1);

    this.store
      .select(socialAppSelectors.isPresentationActiveAndPublishing)
      .pipe(startWith(false), distinctUntilChanged(), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, isPresentationActive]) => {
        // TODO why is this firing twice on room change?
        // Start
        if (!previous && isPresentationActive) {
          this.onStartPresentation();
        }

        // Stop
        if (previous && !isPresentationActive) {
          this.onStopPresentation();
        }
      });

    this.store
      .select(socialAppSelectors.isUserAudioInMix)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isUserAudioInMix) => {
        if (!this.isAudioForwarding && isUserAudioInMix) {
          this.forwardAudio();
        }
        if (this.isAudioForwarding && !isUserAudioInMix) {
          this.stopAudioForwarding();
        }
      });

    this.store
      .select(socialAppSelectors.getMixVolume)
      .pipe(
        tap((volume) => (this.mixVolume = volume)),
        takeUntil(this.destroyed$),
      )
      .subscribe((volume) => this.mixSubscriber?.setVolume(volume));
  }

  public enableCrossRoomMix(): void {
    const msg: StreamingMixModeMessage = {
      t: OutgoingMessageType.STR_MIX,
      mode: MixModeType.CROSS_ROOM,
    };
    this.websocketService.sendMessage(msg);
  }

  public disableCrossRoomMix(): void {
    const msg: StreamingMixModeMessage = {
      t: OutgoingMessageType.STR_MIX,
      mode: MixModeType.IN_ROOM,
    };
    this.websocketService.sendMessage(msg);
  }

  private onStartPresentation(): void {
    this.info('Presentation started');

    this.store.dispatch(socialAppActions.SET_MIX_VOLUME({ volume: DEFAUlT_MIX_VOLUME }));
    this.mixVolume = DEFAUlT_MIX_VOLUME;
    this.mixSubscriber = new MixSubscriber(
      this.videoRoomService,
      this.websocketService,
      this.rtcStatsStreamService,
      this.store,
      this.mixVolume,
      this.videoRoomService.mixStream,
    );
  }

  private onStopPresentation(): void {
    this.info('Presentation ended');

    this.stopAudioForwarding();

    this.mixSubscriber?.destroy();
    this.mixSubscriber = undefined;
  }

  private updateMixVolume(value: number) {
    if (value >= 0 && value <= DEFAUlT_MIX_VOLUME) {
      this.store.dispatch(socialAppActions.SET_MIX_VOLUME({ volume: value }));
      this.mixVolume = value;
    }
  }

  public async transitionFadeOutAudio(delay: number): Promise<void> {
    this.info('Transition audio: Starting transition');
    await this.easingService
      .createEasing(DEFAUlT_MIX_VOLUME, 0, delay, 10, linearEasing)
      .pipe(
        finalize(() => {
          this.updateMixVolume(0);
          return;
        }),
      )
      .forEach((value) => {
        this.updateMixVolume(value);
      });

    this.info('Transition audio: Returning');
  }

  public resetFadedAudioMix(): void {
    this.info('Transition audio: resetting mix volume');
    this.updateMixVolume(DEFAUlT_MIX_VOLUME);
  }

  /**
   * Indicate to the server that this user's audio should be forwarded to the presentation mix.
   */
  private forwardAudio() {
    if (this.isAudioForwarding) {
      return;
    }

    this.info('Forwarding audio');
    // Forward my audio to the mix
    const forwardMsg: StreamingForwardMessage = {
      t: OutgoingMessageType.STR_FWD,
    };
    this.websocketService.sendMessage(forwardMsg);
    this.isAudioForwarding = true;
  }

  /**
   * Indicate to the server that this user's audio should be not be forwarded to the presentation mix.
   */
  private stopAudioForwarding() {
    if (!this.isAudioForwarding) {
      return;
    }

    this.info('Stopping audio forwarding');
    const stopForwarding: StreamingStopForwardingMessage = {
      t: OutgoingMessageType.STR_STOP_FWD,
    };
    this.websocketService.sendMessage(stopForwarding);
    this.isAudioForwarding = false;
  }

  public get subscriber(): MixSubscriber | undefined {
    return this.mixSubscriber;
  }

  /**
   * Deallocate presentation resources
   */
  public destroy(): void {
    this.mixSubscriber?.destroy();
    this.mixSubscriber = undefined;

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
